function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { FunctionalLoader, useOverlayContext } from 'seagull';
import { Page, Typography, Card, Switch, Grid, GridItem, Button, WithInfoIcon, WithStatusIcon, ConfirmationModal } from 'seagull';
import { useEventReporter } from "../../../event-reporter";
import { useStore, useStoreActions } from "../../../store/store-context";
import { useQboIntegration } from "../../../hooks/useQboIntegration";
import { openInNewTabProps, routes } from "../../../constants";
import { helpArticles } from "../../../utils/helpArticles";
import { selectors } from "../../../store/state-selectors";
import { integrationModel, qboIntegrationModel } from "../../../models";
import { Intent } from "../../../infra/access-control";
import { MapServicesModal } from "../../QBO/MapServicesModal/MapServicesModal";
import { MapContactsModal } from "../../QBO/MapContactsModal/MapContactsModal";
import { MapAccountsModal } from "../../QBO/MapAccountsModal/MapAccountsModal";
import styles from "./QboSetupPage.module.scss";
import ContactSupportButton from "../../../components/ContactSupportButton";
import ConditionalWrapper from "../../../components/ConditionalWrapper/ConditionalWrapper";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
const title = 'QuickBooks Online Setup';
const subheader = 'Set up your QuickBooks Online account connection.';
export function QboSetupPage() {
  const {
    showModal,
    showErrorToast
  } = useOverlayContext();
  const {
    setQboIntegration
  } = useStoreActions();
  const isPendingQboIntegrationSetup = useStore(selectors.integration.isPendingQboIntegrationSetup);
  const vendorId = useStore(selectors.user.businessId);
  const userId = useStore(selectors.user.id);
  const {
    updateQboSettings,
    disconnectFromQbo
  } = useQboIntegration();
  const [hasOperationInBackground, setHasOperationInBackground] = useState(false);
  const history = useHistory();
  const qboIntegration = useStore(selectors.integration.qboIntegration);
  const [isLoading, setIsLoading] = useState(false);
  const isLinked = qboIntegration && qboIntegrationModel.isLinked(qboIntegration);
  const automaticInvoicesSync = !!qboIntegration && qboIntegrationModel.automaticInvoicesSync(qboIntegration);
  const allowTimeActivities = useStore(selectors.integration.allowTimeActivities);
  const classTrackingAgreementOverride = !!qboIntegration && qboIntegrationModel.classTrackingAgreementOverride(qboIntegration);
  const {
    reportToAppcues,
    reportToGtm,
    reportToHeap,
    reportToHubspot
  } = useEventReporter();
  const {
    logBI,
    fetchIntegrations
  } = useStoreActions();
  const {
    TIME_ACTIVITIES = false
  } = useFeatureFlags();
  useEffect(() => {
    setIsLoading(true);
    fetchIntegrations().then(integrations => {
      const qboInteg = integrationModel.qboIntegration(integrations);
      const hasIntegration = Boolean(qboInteg);
      const isIntegrationLinkedAndNotReady = qboInteg && qboIntegrationModel.isLinked(qboInteg) && !(qboInteg !== null && qboInteg !== void 0 && qboInteg.isReady);
      if (!hasIntegration || isIntegrationLinkedAndNotReady) {
        history.push(routes.integrations);
      }
    }).finally(() => setIsLoading(false));
  }, []);
  const subHeader = useMemo(() => getSubHeaderByIntegration(qboIntegration), [qboIntegration]);
  const disconnect = async () => {
    const userApprovedDisconnect = await showModal({
      closeOnClickOutside: false,
      closeOnEscapeKey: false,
      content: _ref => {
        let {
          dismissModal
        } = _ref;
        return /*#__PURE__*/React.createElement(ConfirmationModal, {
          "data-testid": "qbo-disconnect-modal",
          title: "Disconnect from QuickBooks Online",
          content: /*#__PURE__*/React.createElement(Typography, null, "Are you sure you want to disconnect from QuickBooks Online?"),
          onCloseButtonClick: () => dismissModal(false),
          onPrimaryButtonClick: () => dismissModal(true),
          primaryButtonLabel: "Disconnect",
          onSecondaryButtonClick: () => dismissModal(false),
          secondaryButtonLabel: "Cancel",
          skin: "danger"
        });
      }
    });
    if (userApprovedDisconnect) {
      logBI(12003, {
        vendorId,
        userId
      });
      reportToAppcues('button-click', 'qbo-vendor-disconnect-connection');
      reportToGtm('button-click', 'qbo-vendor-disconnect-connection', {
        vendorId,
        userId
      });
      reportToHubspot('vendor_disconnects_qbo_connection');
      await disconnectFromQbo();
      history.push(routes.integrations);
    }
  };
  function toggleAutoSyncInvoices(autoSync) {
    if (!qboIntegration) return;
    const updated = qboIntegrationModel.setAutomaticInvoicesSync(qboIntegration, autoSync);
    updateSettings(updated);
  }
  function toggleDisplayBillableTimeActivities(displayBillableTimeActivities) {
    if (!qboIntegration) return;
    const updated = qboIntegrationModel.setDisplayBillableTimeActivities(qboIntegration, displayBillableTimeActivities);
    updateSettings(updated);
  }
  const chooseAccounts = async () => {
    logBI(12010, {
      vendorId,
      userId
    });
    reportToHeap('integrations', 'click', 'qbo-choose-accounts');
    reportToHubspot('vendor_started_contact_sync');
    await showModal({
      closeOnClickOutside: false,
      closeOnEscapeKey: false,
      content: _ref2 => {
        let {
          dismissModal
        } = _ref2;
        return /*#__PURE__*/React.createElement(MapAccountsModal, {
          dismissModal: dismissModal
        });
      }
    });
  };
  const mapContacts = async () => {
    logBI(12001, {
      vendorId,
      userId
    });
    reportToAppcues('button-click', 'qbo-vendor-started-contacts-map');
    reportToGtm('button-click', 'qbo-vendor-started-contacts-map', {
      vendorId,
      userId
    });
    reportToHeap('integrations', 'click', 'qbo-map-contacts');
    reportToHubspot('vendor_started_contact_sync');
    await showModal({
      closeOnClickOutside: false,
      closeOnEscapeKey: false,
      content: _ref3 => {
        let {
          dismissModal
        } = _ref3;
        return /*#__PURE__*/React.createElement(MapContactsModal, {
          dismissModal: dismissModal
        });
      }
    });
  };
  const mapServices = async () => {
    logBI(12002, {
      vendorId,
      userId
    });
    reportToAppcues('button-click', 'qbo-vendor-started-services-map');
    reportToGtm('button-click', 'qbo-vendor-started-services-map', {
      vendorId,
      userId
    });
    reportToHeap('integrations', 'click', 'qbo-map-services');
    reportToHubspot('vendor_started_services_sync');
    await showModal({
      closeOnClickOutside: false,
      closeOnEscapeKey: false,
      content: _ref4 => {
        let {
          dismissModal
        } = _ref4;
        return /*#__PURE__*/React.createElement(MapServicesModal, {
          dismissModal: dismissModal
        });
      }
    });
  };
  function toggleClassOverrides(classOverride) {
    if (!qboIntegration) return;
    const updated = qboIntegrationModel.setClassTrackingAgreementOverride(qboIntegration, classOverride);
    updateSettings(updated);
  }
  async function updateSettings(updated) {
    setHasOperationInBackground(true);
    const {
      success
    } = await updateQboSettings(updated.settings);
    if (success) {
      setQboIntegration(updated);
    } else {
      showErrorToast('Failed to update settings');
    }
    setHasOperationInBackground(false);
  }
  return /*#__PURE__*/React.createElement(Page, {
    "data-testid": "qbo-setup-page",
    header: /*#__PURE__*/React.createElement("div", {
      className: styles.pageHeader
    }, title),
    subheader: /*#__PURE__*/React.createElement("div", {
      className: styles.pageSubHeader
    }, subheader, /*#__PURE__*/React.createElement(Typography, _extends({
      as: "a",
      href: helpArticles.qboSetup()
    }, openInNewTabProps), "Learn more")),
    loading: isLoading,
    onClickBack: () => {
      history.push(routes.integrations);
    }
  }, /*#__PURE__*/React.createElement(FunctionalLoader, {
    show: hasOperationInBackground,
    name: "integrationOperation"
  }), /*#__PURE__*/React.createElement("div", {
    className: styles.pageContainer
  }, /*#__PURE__*/React.createElement(Section, {
    testid: "connection-settings",
    header: "Connection settings",
    subHeader: subHeader,
    isError: !qboIntegration || (qboIntegration === null || qboIntegration === void 0 ? void 0 : qboIntegration.status) === 'error'
  }, /*#__PURE__*/React.createElement(GridItemLeft, null, /*#__PURE__*/React.createElement(ConditionalWrapper, {
    condition: isPendingQboIntegrationSetup,
    wrapper: children => /*#__PURE__*/React.createElement(WithStatusIcon, {
      "data-testid": "missing-account-mapping-error",
      info: "Selecting accounts is mandatory for this integration to work"
    }, children)
  }, /*#__PURE__*/React.createElement(Typography, null, "Select which QuickBooks accounts will be used"))), /*#__PURE__*/React.createElement(GridItemRight, null, /*#__PURE__*/React.createElement(Button, {
    className: styles.button,
    "data-testid": "choose-accounts",
    intent: Intent.QboMapAccounts,
    onClick: chooseAccounts,
    disabled: (qboIntegration === null || qboIntegration === void 0 ? void 0 : qboIntegration.status) === 'error'
  }, "Choose accounts")), isLinked && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(GridItemLeft, null, /*#__PURE__*/React.createElement(Typography, null, "Map Anchor contacts to QuickBooks customers")), /*#__PURE__*/React.createElement(GridItemRight, null, /*#__PURE__*/React.createElement(Button, {
    className: styles.button,
    disabled: isPendingQboIntegrationSetup,
    "data-testid": "map-contacts",
    intent: Intent.QboSetupMapContacts,
    onClick: mapContacts
  }, "Map contacts")), /*#__PURE__*/React.createElement(GridItemLeft, null, /*#__PURE__*/React.createElement(Typography, null, "Map Anchor services to QuickBooks services")), /*#__PURE__*/React.createElement(GridItemRight, null, /*#__PURE__*/React.createElement(Button, {
    className: styles.button,
    disabled: isPendingQboIntegrationSetup,
    "data-testid": "map-services",
    intent: Intent.QboMapServices,
    onClick: mapServices
  }, "Map services")))), /*#__PURE__*/React.createElement(Section, {
    testid: "advanced-settings",
    header: "Advanced settings"
  }, /*#__PURE__*/React.createElement(GridItemLeft, null, /*#__PURE__*/React.createElement(WithInfoIcon, {
    info: "Invoices, payments and deposits will not be synced automatically to QuickBooks Online. A manual sync option will be available on each item."
  }, /*#__PURE__*/React.createElement(Typography, null, "Manually sync items to QuickBooks Online (not recommended)"))), /*#__PURE__*/React.createElement(GridItemRight, null, /*#__PURE__*/React.createElement(Switch, {
    checked: !automaticInvoicesSync,
    disabled: !isLinked,
    "data-testid": "sync-items-manually-switch",
    onChange: (_, manualSync) => toggleAutoSyncInvoices(!manualSync)
  })), TIME_ACTIVITIES && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(GridItemLeft, null, /*#__PURE__*/React.createElement(WithInfoIcon, {
    info: "Billable time activities from QuickBooks Online will not be displayed in Anchor when this setting is enabled."
  }, /*#__PURE__*/React.createElement(Typography, null, "Do not display billable time activities"))), /*#__PURE__*/React.createElement(GridItemRight, null, /*#__PURE__*/React.createElement(Switch, {
    checked: !allowTimeActivities,
    disabled: !isLinked,
    "data-testid": "display-billable-time-activities-switch",
    onChange: (_, checked) => toggleDisplayBillableTimeActivities(!checked)
  }))), /*#__PURE__*/React.createElement(GridItemLeft, null, /*#__PURE__*/React.createElement(WithInfoIcon, {
    info: "If this feature is activated, the default Class assigned to services can be overridden on a per-agreement basis."
  }, /*#__PURE__*/React.createElement(Typography, null, "Allow assigning classes to services on a per-agreement basis"))), /*#__PURE__*/React.createElement(GridItemRight, null, /*#__PURE__*/React.createElement(Switch, {
    checked: classTrackingAgreementOverride,
    disabled: !isLinked,
    "data-testid": "class-overrides-switch",
    onChange: (_, checked) => toggleClassOverrides(checked)
  }))), /*#__PURE__*/React.createElement(Section, {
    testid: "disconnect-qbo"
  }, /*#__PURE__*/React.createElement(GridItemLeft, null, /*#__PURE__*/React.createElement(Typography, {
    skin: "error"
  }, "Disconnect from QuickBooks Online")), /*#__PURE__*/React.createElement(GridItemRight, null, /*#__PURE__*/React.createElement(Button, {
    className: styles.button,
    skin: "danger",
    "data-testid": "disconnect",
    intent: Intent.QboDisconnect,
    onClick: disconnect,
    disabled: qboIntegration && qboIntegrationModel.isErrored(qboIntegration)
  }, "Disconnect")))));
}
const getSubHeaderByIntegration = integration => {
  if (integration && qboIntegrationModel.isErrored(integration)) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, "Failed to connect. Please ", /*#__PURE__*/React.createElement(ContactSupportButton, {
      skin: "error",
      size: "small"
    }), ".");
  }
  return '';
};
const Section = props => {
  const {
    testid,
    header,
    subHeader,
    isError,
    children
  } = props;
  return /*#__PURE__*/React.createElement(Card, {
    "data-testid": "".concat(testid, "-section"),
    className: styles.section
  }, header && /*#__PURE__*/React.createElement("div", {
    className: styles.header
  }, /*#__PURE__*/React.createElement(Typography, {
    "data-testid": "".concat(testid, "-header"),
    weight: "normal",
    variant: "h5"
  }, header), subheader && /*#__PURE__*/React.createElement(Typography, {
    skin: isError ? 'error' : 'secondary',
    variant: "paragraph2",
    "data-testid": "".concat(testid, "-sub-header")
  }, subHeader)), /*#__PURE__*/React.createElement(Grid, {
    align: "center",
    rowGap: "small"
  }, children));
};
const GridItemRight = _ref5 => {
  let {
    children
  } = _ref5;
  return /*#__PURE__*/React.createElement(GridItem, {
    span: 2,
    justify: "center"
  }, children);
};
const GridItemLeft = _ref6 => {
  let {
    children
  } = _ref6;
  return /*#__PURE__*/React.createElement(GridItem, {
    span: 10
  }, children);
};